import { useCallback, useEffect, useRef, useState } from "react";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { Search, Loader2, Check, AlertTriangle } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  registrySearch,
  registryDetails,
  checkDuplicate,
} from "services/customers.service";

export interface RegistryMatch {
  companyCode: string;
  // The registered name in full, e.g. UAB "Hilla" — what the picker shows,
  // because that is what a manager recognises among similar rows.
  displayName?: string;
  // The two the form keeps apart: name = Hilla, legalName = UAB.
  name: string;
  legalName?: string;
  vatCodeLetters: string;
  vatCodeFigures: string;
  isVatPayer: boolean;
  legalForm?: string;
  isActive: boolean;
  country?: string;
  // Some member states answer "this VAT number is valid" while withholding the
  // name and address — Germany does. Flagged so the screen can say so instead
  // of showing an empty row that looks broken.
  detailsWithheld?: boolean;
  address?: string;
  street?: string;
  houseNumber?: string;
  city?: string;
  region?: string;
  // Only the late address answer carries them (Lithuanian companies, from the
  // company register); the search rows do not.
  zipCodeLetters?: string;
  zipCodeFigures?: string;
}

interface ExistingCustomer {
  id: string;
  name: string;
  vatCode?: string;
  companyCode?: string;
}

interface Props {
  companyId: string;
  onSelect: (match: RegistryMatch, opts?: { addressOnly?: boolean }) => void;
}

/**
 * Find a counterparty in the public registers and drop its details into the
 * form, so a manager does not retype what the state already publishes.
 *
 * One box takes whichever identifier they happen to have — the company name,
 * the company code, or the VAT code.
 */
export default function CustomerRegistrySearch({ companyId, onSelect }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<RegistryMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filled, setFilled] = useState(false);
  const [existing, setExisting] = useState<ExistingCustomer | null>(null);
  const [withheld, setWithheld] = useState(false);

  // Lets a slow answer to an earlier keystroke be discarded instead of
  // overwriting the results of a later one.
  const seqRef = useRef(0);
  // The same protection for the two calls a pick fires off. Without it a slow
  // answer for an earlier company lands on top of a newer choice — or on top
  // of corrections the manager has already typed.
  const pickRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const seq = ++seqRef.current;
    // Wait for a pause in typing: one request per word, not per letter.
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await registrySearch(companyId, q);
        if (seq !== seqRef.current) return;
        setResults(data.results || []);
      } catch (e: any) {
        if (seq !== seqRef.current) return;
        setResults([]);
        setError(
          e?.response?.status === 503
            ? t("registry_unavailable")
            : t("registry_failed")
        );
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [query, companyId, t]);

  const choose = useCallback(
    async (match: RegistryMatch | null) => {
      if (!match) return;
      const pick = ++pickRef.current;

      setQuery(match.displayName || match.name);
      setFilled(true);
      setExisting(null);
      setWithheld(Boolean(match.detailsWithheld));

      // Fill what is already known at once; the address is a second call, so
      // the form must not sit empty while it runs.
      onSelect(match);

      // Say so at the first click, not after everything else has been typed.
      checkDuplicate(
        companyId,
        `${match.vatCodeLetters}${match.vatCodeFigures}`,
        match.companyCode
      )
        .then((d) => {
          if (pick !== pickRef.current) return;
          setExisting(d.existing || null);
        })
        // A check that cannot run must not stop anyone creating a customer;
        // the server refuses a real duplicate on save regardless.
        .catch(() => {
          if (pick !== pickRef.current) return;
          setExisting(null);
        });

      // A Lithuanian company has an address under its company code even
      // without a VAT code; a foreign one needs its VAT code.
      if (!match.companyCode && !match.vatCodeFigures) return;

      try {
        const { address } = await registryDetails(companyId, {
          companyCode: match.companyCode,
          vatCodeLetters: match.vatCodeLetters,
          vatCodeFigures: match.vatCodeFigures,
        });
        if (pick !== pickRef.current) return;
        if (address) onSelect({ ...match, ...address }, { addressOnly: true });
      } catch {
        // No address is an ordinary outcome, not a failed lookup — everything
        // else has already been filled in.
      }
    },
    [companyId, onSelect]
  );

  return (
    <div className="mb-4">
      <Combobox<RegistryMatch | null> value={null} onChange={choose} immediate>
        <label className="block text-sm/6 font-medium text-zinc-950 dark:text-white">
          {t("registry_search_label")}
        </label>

        <div className="relative mt-1">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-400">
            <Search className="size-4" aria-hidden="true" />
          </span>
          <ComboboxInput
            className={clsx(
              "block w-full rounded-lg border border-zinc-950/10 bg-white py-2 pl-9 pr-9",
              "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6",
              "focus:outline-none focus:ring-2 focus:ring-blue-500",
              "dark:border-white/10 dark:bg-white/5 dark:text-white"
            )}
            placeholder={t("registry_search_placeholder") as string}
            autoComplete="off"
            displayValue={() => query}
            onChange={(e) => {
              setQuery(e.target.value);
              setFilled(false);
              setExisting(null);
              setWithheld(false);
              // A late answer for a previous pick must not land on what is
              // being typed now.
              pickRef.current += 1;
            }}
          />
          {loading && (
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            </span>
          )}
        </div>

        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          {t("registry_search_help")}{" "}
          {t("registry_search_help_foreign")}
        </p>

        {query.trim().length >= 3 && !loading && (
          <ComboboxOptions
            anchor="bottom start"
            // Portalled to document.body, and this form is mounted inside a
            // Polaris Modal whose overlay sits at z-index 518. Anything lower
            // renders behind it: the list would be invisible and unclickable.
            className="z-[9999] w-[var(--input-width)] rounded-lg border border-zinc-950/10 bg-white p-1 shadow-lg empty:hidden dark:border-white/10 dark:bg-zinc-800"
          >
            {error || !results.length ? (
              <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                {error || t("registry_no_results")}
              </div>
            ) : (
              results.map((r) => (
                <ComboboxOption
                  key={r.companyCode}
                  value={r}
                  className="group flex cursor-default flex-col gap-0.5 rounded-md px-3 py-2 data-[focus]:bg-blue-500 data-[focus]:text-white"
                >
                  <span className="text-sm font-medium">
                    {r.displayName || r.name || t("registry_details_withheld_short")}
                    {!r.isActive && (
                      <span className="ml-2 text-xs font-normal text-amber-600 group-data-[focus]:text-amber-200">
                        {t("registry_inactive")}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-zinc-500 group-data-[focus]:text-blue-100">
                    {r.companyCode ? `${r.companyCode} · ` : ""}
                    {r.vatCodeFigures
                      ? `${r.vatCodeLetters}${r.vatCodeFigures}`
                      : t("registry_not_vat_payer")}
                  </span>
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        )}
      </Combobox>

      {existing && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <span className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            {t("registry_already_exists")}
          </span>
          <span className="mt-0.5 block">
            {existing.name}
            {existing.vatCode ? ` · ${existing.vatCode}` : ""}
          </span>
        </div>
      )}

      {withheld && (
        <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-300">
          {t("registry_details_withheld")}
        </p>
      )}

      {filled && !existing && !withheld && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
          <Check className="size-3.5" aria-hidden="true" />
          {t("registry_filled_from_public_data")}
        </p>
      )}
    </div>
  );
}
